
import { Brain, Search, User, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const Header = () => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/games?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
    }
  };

  const handleStartTraining = () => {
    navigate('/games');
  };

  return (
    <header className="w-full bg-gradient-to-r from-brain-purple/5 via-white/95 to-brain-teal/5 backdrop-blur-md sticky top-0 z-50 border-b border-brain-light-purple/30 py-4 px-4 md:px-8 shadow-sm">
      <div className="max-w-7xl mx-auto flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Link to="/" className="flex items-center gap-2">
            <div className="relative">
              <div className="absolute inset-0 bg-brain-purple/20 rounded-full blur-md"></div>
              <Brain size={32} className="text-brain-purple relative z-10" />
            </div>
            <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-brain-purple to-brain-teal bg-clip-text text-transparent">
              Brain Game
            </span>
          </Link>
        </div>
        
        <div className="hidden md:flex items-center gap-4 flex-1 max-w-xs mx-6">
          <form className="relative w-full group" onSubmit={handleSearch}>
            <Search size={18} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground group-hover:text-brain-purple transition-colors" />
            <Input 
              placeholder="Search games..." 
              className="pl-8 bg-white/70 border-brain-light-purple/20 focus-visible:ring-brain-purple transition-all shadow-sm hover:shadow focus:shadow"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </form>
        </div>
        
        <nav className="hidden md:flex items-center gap-8">
          <Link 
            to="/games" 
            className={cn(
              "font-medium transition-colors hover:text-brain-purple relative group",
              isActive("/games") ? "text-brain-purple" : "text-foreground"
            )}
          >
            Games
            <span className={cn(
              "absolute -bottom-1.5 left-0 w-0 h-0.5 bg-brain-purple transition-all duration-300 group-hover:w-full",
              isActive("/games") ? "w-full" : ""
            )}></span>
          </Link>
          {/* <Link 
            to="/progress" 
            className={cn(
              "font-medium transition-colors hover:text-brain-purple relative group",
              isActive("/progress") ? "text-brain-purple" : "text-foreground"
            )}
          >
            Progress
            <span className={cn(
              "absolute -bottom-1.5 left-0 w-0 h-0.5 bg-brain-purple transition-all duration-300 group-hover:w-full",
              isActive("/progress") ? "w-full" : ""
            )}></span>
          </Link> */}
          <Link 
            to="/about" 
            className={cn(
              "font-medium transition-colors hover:text-brain-purple relative group",
              isActive("/about") ? "text-brain-purple" : "text-foreground"
            )}
          >
            About
            <span className={cn(
              "absolute -bottom-1.5 left-0 w-0 h-0.5 bg-brain-purple transition-all duration-300 group-hover:w-full",
              isActive("/about") ? "w-full" : ""
            )}></span>
          </Link>
        </nav>
        
        <div className="flex items-center gap-3">
          {/* <Button 
            variant="outline" 
            size="icon" 
            className="rounded-full border-brain-light-purple/30 hidden md:flex bg-white/70 hover:bg-brain-purple/10 hover:text-brain-purple hover:border-brain-purple/50 transition-colors"
            onClick={() => navigate('/profile')}
          >
            <User size={18} />
          </Button> */}
          <Button 
            className="bg-gradient-to-r from-brain-purple to-brain-vivid-purple hover:opacity-90 shadow-md hover:shadow-lg text-white hidden md:flex items-center gap-2 transition-all"
            onClick={handleStartTraining}
          >
            Start Training
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className="md:hidden text-brain-purple hover:bg-brain-purple/10"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
          >
            <Menu size={24} />
          </Button>
        </div>
      </div>
      
      {showMobileMenu && (
        <div className="md:hidden absolute top-full left-0 w-full bg-white/95 backdrop-blur-md border-b border-brain-light-purple/20 animate-fade-in shadow-md z-50">
          <div className="p-4 flex flex-col gap-4">
            <form className="relative w-full" onSubmit={handleSearch}>
              <Search size={18} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input 
                placeholder="Search games..." 
                className="pl-8 bg-white border-brain-light-purple/20"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </form>
            <nav className="flex flex-col gap-2">
              <Link 
                to="/games" 
                className={cn(
                  "p-2.5 rounded-md transition-colors hover:bg-brain-purple/10 hover:text-brain-purple",
                  isActive("/games") ? "bg-brain-purple/10 text-brain-purple" : ""
                )}
              >
                Games
              </Link>
              {/* <Link 
                to="/progress" 
                className={cn(
                  "p-2.5 rounded-md transition-colors hover:bg-brain-purple/10 hover:text-brain-purple",
                  isActive("/progress") ? "bg-brain-purple/10 text-brain-purple" : ""
                )}
              >
                Progress
              </Link> */}
              <Link 
                to="/about" 
                className={cn(
                  "p-2.5 rounded-md transition-colors hover:bg-brain-purple/10 hover:text-brain-purple",
                  isActive("/about") ? "bg-brain-purple/10 text-brain-purple" : ""
                )}
              >
                About
              </Link>
            </nav>
            <Button 
              className="w-full bg-gradient-to-r from-brain-purple to-brain-vivid-purple hover:opacity-90 text-white"
              onClick={handleStartTraining}
            >
              Start Training
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
